<template>
  <div class="shift-table">
    <el-skeleton :loading="loading" animated>
      <template #template>
        <div v-for="n in 3" :key="n" class="user-row skeleton">
          <div class="avatar" />
          <div class="info">
            <div class="name" />
            <div class="shift" v-for="i in 2" :key="i" />
          </div>
        </div>
      </template>

      <template #default>
        <el-table :data="users" style="width: 100%">
          <el-table-column label="Сотрудник">
            <template #default="{ row }">
              <div class="shift-employee-cell">
                <el-avatar :size="34" v-if="row.avatar" :src="row.avatar" />
                <el-avatar :size="34" v-else>{{ getInitials(row.name) }}</el-avatar>
                <span class="shift-employee-name">{{ row.name }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- Локация -->
          <el-table-column label="Локация">
            <template #default="{ row }">
              <div class="shift-location-cell">
                <span class="shift-location-icon">📍</span>
                <span>
                  {{ row.city_name || row.city_id }}
                  → {{ row.branch_name || row.branch_id }}
                  <span v-if="row.room_name || row.room_id"> → {{ row.room_name || row.room_id }}</span>
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- Смены -->
          <el-table-column label="Смены" width="160">
            <template #default="{ row }">
              <el-button class="show-shifts-btn" size="small" type="primary" @click="openCalendar(row)">
                Показать смены
              </el-button>
            </template>
          </el-table-column>

          <!-- Действия -->
          <el-table-column v-if="type === 'current'" label="Действия" width="120" align="center">
            <template #default="{ row }">
              <div class="shift-actions-cell">
                <el-button
                  type="primary"
                  size="small"
                  :icon="Edit"
                  circle
                  @click="openCalendar(row, true)"
                />
                <el-button
                  type="danger"
                  size="small"
                  :icon="Delete"
                  circle
                  @click="$emit('delete-shifts', row)"
                />
              </div>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </el-skeleton>
  </div>
  <el-dialog
    v-model="calendarVisible"
    class="shift-view-dialog"
    width="760px"
    :close-on-click-modal="false">
    <template #header="{ titleId, titleClass }">
      <div :id="titleId" :class="titleClass" class="shift-view-dialog-header">
        <span class="shift-view-dialog-title">{{ selectedUser ? `Смены — ${selectedUser.name}` : 'Смены сотрудника' }}</span>
        <el-button
          v-if="calendarMode === 'edit'"
          type="primary"
          plain
          size="small"
          @click="applyTemplateDialogVisible = true"
        >
          Применить шаблон
        </el-button>
      </div>
    </template>

    <shift-calendar
      :shifts="selectedUser?.shifts || []"
      :user-name="selectedUser?.name || ''"
      :mode="calendarMode"
      :working-hours="selectedUser?.working_hours || null"
      @update:shifts="val => selectedUser.shifts = val"
    />
    <template #footer v-if="calendarMode === 'edit'">
      <div class="d-flex justify-content-end mb-2">
        <el-button type="primary" @click="$emit('update-shifts', selectedUser)">
          Обновить смены
        </el-button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="applyTemplateDialogVisible"
    title="Применить шаблон"
    width="360"
    class="shift-template-apply-dialog"
  >
    <el-select v-model="selectedTemplateId" placeholder="Выберите шаблон" style="width: 100%; margin-bottom: 12px;">
      <el-option
        v-for="t in templates"
        :key="t.id"
        :label="t.name"
        :value="t.id"
      />
    </el-select>
    <el-date-picker
      v-model="dateRange"
      type="daterange"
      start-placeholder="Дата начала"
      end-placeholder="Дата окончания"
      :disabled-date="disabledPastDates"
      range-separator="—"
      unlink-panels
      style="width: 100%; margin-bottom: 12px;"
    />
    <div class="d-flex justify-content-end">
      <el-button type="primary" @click="applyTemplateSubmit">Применить</el-button>
    </div>
  </el-dialog>
</template>

<script>
import ShiftCalendar from './ShiftCalendar.vue';
import { Delete, Edit } from '@element-plus/icons-vue'
import { mapState } from 'vuex'
import { generateShiftsFromTemplate } from '@/components/util/scheduleTemplate'

export default {
  emits: ['update-shifts', 'delete-shifts', 'apply-template'],
  computed: {
    ...mapState('cities', ['cities']),
    ...mapState('schedules', { templates: state => state.schedulesTemplates }),
    Delete() { return Delete },
    Edit() { return Edit }
  },
  components: { ShiftCalendar },
  props: {
    users: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    type: { type: String, default: 'current' }, // current | history
  },
  data() {
    return {
      calendarVisible: false,
      selectedUser: null,
      calendarMode: 'view',
      applyTemplateDialogVisible: false,
      selectedTemplateId: null,
      dateRange: [],
    };
  },
  methods: {
    getInitials(name) {
      if (!name || typeof name !== 'string') return ''
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    },
    resolveWorkingHours(user) {
      const list = Array.isArray(this.cities) ? this.cities : (Array.isArray(this.cities?.list) ? this.cities.list : []);
      if (!list || !list.length) return null;

      const cityId = user.city_id;
      const branchId = user.branch_id;
      const roomId = user.room_id;

      const city = list.find(c => c.id == cityId);
      if (!city) return null;

      const branch = city.children?.find(b => b.id == branchId);
      if (!branch) return null;

      if (roomId) {
        const room = branch.children?.find(r => r.id == roomId);
        if (room && room.working) return room.working;
      }

      return branch.working || null;
    },
    openCalendar(user, isEdit = false) {
      const workingHours = this.resolveWorkingHours(user);
      this.selectedUser = {
        ...user,
        working_hours: workingHours
      };
      this.calendarMode = isEdit ? 'edit' : 'view';
      this.calendarVisible = true;
    },
    disabledPastDates(date) {
      const today = new Date();
      today.setHours(0,0,0,0);
      return date.getTime() < today.getTime();
    },
    applyTemplateSubmit() {
      if (!this.selectedTemplateId || !this.dateRange.length || !this.selectedUser) {
        this.$message.error('Выберите шаблон и диапазон дат');
        return;
      }

      const template = this.templates.find(t => t.id === this.selectedTemplateId);
      if (!template) {
        this.$message.error('Шаблон не найден');
        return;
      }

      const [startDate, endDate] = this.dateRange;
      const workingHours = this.selectedUser.working_hours;

      if (!workingHours) {
        this.$message.error('Не указаны рабочие часы для сотрудника');
        return;
      }

      // 1️⃣ Удаляем все смены, которые начинаются с выбранной даты
      this.selectedUser.shifts = (this.selectedUser.shifts || []).filter(s => s.date < startDate);

      // 2️⃣ Генерируем новые смены по шаблону
      const newShifts = generateShiftsFromTemplate(
        template,
        startDate,
        endDate,
        workingHours
      );

      // 3️⃣ Добавляем их к текущим
      this.selectedUser.shifts.push(...newShifts);

      // 4️⃣ Уведомление
      this.$message.success('Шаблон успешно применён');

      // 5️⃣ Очистка формы
      this.selectedTemplateId = null;
      this.dateRange = [];
      this.applyTemplateDialogVisible = false;
    },
  }
};
</script>
<style scoped>
.shift-employee-cell {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}

.shift-employee-name {
  color: var(--cal-accent);
}

.shift-location-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--cal-accent);
}

.shift-location-icon {
  opacity: 0.7;
}

.shift-actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.shift-view-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.shift-view-dialog-title {
  display: block;
  font-size: 22px;
  line-height: 1;
  color: var(--cal-text-inverse);
}

.shift-table :deep(.el-table) {
  --el-table-bg-color: var(--cal-bg-cell-alt);
  --el-table-tr-bg-color: transparent;
  --el-table-row-hover-bg-color: rgba(25, 45, 90, 0.92);
  --el-table-header-bg-color: transparent;
  --el-table-border-color: transparent;
  --el-table-text-color: var(--cal-text-regular);
  --el-table-header-text-color: var(--cal-text-inverse);
  background: var(--cal-bg-cell-alt);
  color: var(--cal-text-regular);
  border: 0;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.shift-table :deep(.el-table::before),
.shift-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.shift-table :deep(.el-table table) {
  border-collapse: collapse;
  border-spacing: 0;
}

.shift-table :deep(.el-table th.el-table__cell) {
  background: transparent;
  border-bottom: 0;
  padding: 0 7px 14px;
}

.shift-table :deep(.el-table th.el-table__cell .cell) {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    linear-gradient(rgb(16 53 117 / 49%), rgb(16 53 117 / 49%)),
    linear-gradient(151deg, #15264e 0%, #030b1b 75%);
  box-shadow: inset 0 0 0 1px rgba(122, 164, 233, 0.16);
  border-radius: 10px;
  color: var(--cal-text-inverse);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
}

.shift-table :deep(.el-table td.el-table__cell) {
  background: transparent;
  border-bottom: 0;
  padding: 0 18px;
}

.shift-table :deep(.el-table td.el-table__cell .cell) {
  min-height: 54px;
  display: flex;
  align-items: center;
  background: transparent;
  border-radius: 0;
  padding: 0;
  color: var(--cal-text-regular);
}

.shift-table :deep(.el-avatar) {
  background: var(--appointment-primary);
  color: var(--appointment-primary-text);
}

.shift-table :deep(.el-button--primary:not(.is-plain)) {
  background: var(--appointment-primary);
  border-color: transparent;
  color: var(--appointment-primary-text);
}

.shift-table :deep(.el-button--danger) {
  background: var(--appointment-control);
  border-color: transparent;
  color: #ff8d8d;
}

.shift-table :deep(.el-button.is-circle) {
  background: var(--appointment-control);
  border-color: transparent;
  color: var(--cal-text-primary);
}

.shift-table :deep(.show-shifts-btn.el-button) {
  min-height: 34px;
  padding: 0 14px;
  border-radius: 8px;
}

.shift-table :deep(.el-input__wrapper),
.shift-table :deep(.el-select__wrapper),
.shift-table :deep(.el-date-editor.el-input__wrapper) {
  background: var(--appointment-value);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
}

.shift-table :deep(.el-input__inner),
.shift-table :deep(.el-select__placeholder),
.shift-table :deep(.el-select__selected-item),
.shift-table :deep(.el-range-input) {
  color: var(--cal-text-inverse);
}

.shift-table :deep(.el-button--default) {
  background: var(--appointment-secondary);
  border-color: transparent;
  color: var(--appointment-secondary-text);
}

.shift-table :deep(.el-dialog__footer .el-button--primary),
.shift-table :deep(.el-dialog__header .el-button--primary),
.shift-table :deep(.el-dialog .el-button--primary.is-plain) {
  background: var(--appointment-primary);
  border-color: transparent;
  color: var(--appointment-primary-text);
}
</style>
<style>
.shift-view-dialog {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
  box-shadow: var(--cal-shadow-card);
}

.shift-view-dialog .el-dialog__header,
.shift-view-dialog .el-dialog__body,
.shift-view-dialog .el-dialog__footer,
.shift-template-apply-dialog .el-dialog__header,
.shift-template-apply-dialog .el-dialog__body,
.shift-template-apply-dialog .el-dialog__footer {
  background: transparent;
}

.shift-view-dialog .el-dialog__header {
  padding: 24px 32px 8px;
}

.shift-view-dialog .el-dialog__body {
  padding: 0 32px 18px;
}

.shift-view-dialog .el-dialog__footer {
  padding: 10px 32px 24px;
}

.shift-view-dialog .el-dialog__title,
.shift-template-apply-dialog .el-dialog__title {
  color: var(--cal-text-inverse);
}

.shift-view-dialog .el-dialog__headerbtn,
.shift-template-apply-dialog .el-dialog__headerbtn {
  top: 22px;
  right: 22px;
}

.shift-view-dialog .el-dialog__close,
.shift-template-apply-dialog .el-dialog__close {
  color: rgba(13, 22, 49, 0.92);
  font-size: 18px;
}

.shift-template-apply-dialog {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
  box-shadow: var(--cal-shadow-card);
}

.shift-template-apply-dialog .el-input__wrapper,
.shift-template-apply-dialog .el-select__wrapper,
.shift-template-apply-dialog .el-date-editor.el-input__wrapper {
  background: var(--appointment-value);
  box-shadow: 0 0 0 1px var(--cal-border) inset;
}

.shift-template-apply-dialog .el-input__inner,
.shift-template-apply-dialog .el-select__placeholder,
.shift-template-apply-dialog .el-select__selected-item,
.shift-template-apply-dialog .el-range-input {
  color: var(--cal-text-inverse);
}

.shift-template-apply-dialog .el-button--primary {
  background: var(--appointment-primary);
  border-color: transparent;
  color: var(--appointment-primary-text);
}

@media (max-width: 860px) {
  .shift-view-dialog {
    width: min(760px, calc(100vw - 24px)) !important;
  }

  .shift-view-dialog .el-dialog__header,
  .shift-view-dialog .el-dialog__body,
  .shift-view-dialog .el-dialog__footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
